import { Injectable } from "@angular/core";
import {
  NotificationModel,
  NotificationResponse,
} from "../Pages/notification-list/notification.model";
import {
  NotificationAudience,
  NotificationPlacement,
  NotificationStorageEnum,
  PriorityEnum,
} from "../shared/common/common.enum";
import { ApiClient } from "./api-client";
import { BehaviorSubject, Observable } from "rxjs";
import { GenericResponse } from "../shared/common/api-response.model";

export type NotificationPriority =
  | PriorityEnum.Info
  | PriorityEnum.Warning
  | PriorityEnum.Critical;

export interface NotificationPayload {
  id: string; // unique id
  title?: string;
  message: string;
  priority: NotificationPriority;
  createdAt?: number; // epoch ms
  actionLabel?: string; // e.g., "Details"
  actionHandler?: () => void;
  sticky?: boolean; // defaults to true for Home page
}

export interface LoginNotification {
  title: string;
  message: string;
  priority: NotificationPriority;
  createdAt?: number; // epoch ms
}

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private queue: NotificationPayload[] = [];
  private closeNotification: string[] = [];
  private notificationsSubject = new BehaviorSubject<NotificationPayload[]>([]);
  notifications$ = this.notificationsSubject.asObservable();
  private loginNotificationQueue: LoginNotification[] = [];
  private loginNotificationsSubject = new BehaviorSubject<LoginNotification[]>(
    []
  );
  loginNotifications$ = this.loginNotificationsSubject.asObservable();

  constructor(private api: ApiClient) {
    const notify = localStorage.getItem(
      NotificationStorageEnum.closeNotification
    );
    if (notify) {
      this.closeNotification = JSON.parse(notify);
    }
  }

  submitUserNotification(
    payload: NotificationModel
  ): Observable<GenericResponse<NotificationModel>> {
    return this.api.post(`Home/InsertUserNotification`, payload);
  }

  getUserNotification(
    userType = ""
  ): Observable<GenericResponse<NotificationModel[]>> {
    return this.api.get(`Home/GetUserNotification?userType=${userType}`);
  }

  getNotificationForLogin(
    userType: string = "All"
  ): Observable<GenericResponse<NotificationResponse[]>> {
    return this.api.get(`Auth/GetLoginNotification?userType=${userType}`);
  }

  getNotification(
    audience: string = NotificationAudience.all,
    placement: string = NotificationPlacement.dashboard
  ): Observable<GenericResponse<NotificationResponse[]>> {
    return this.api.get(
      `Home/GetNotification?audience=${audience}&placement=${placement}`
    );
  }

  pushLoginNotification(notification: LoginNotification) {
    this.loginNotificationQueue.push(notification);
    this.loginEmitSorted();
  }

  /** Add notification (enqueue). Defaults to sticky and timestamped. */
  push(
    input: Omit<NotificationPayload, "id" | "createdAt" | "sticky"> & {
      id?: string;
      sticky?: boolean;
    }
  ) {
    const payload: NotificationPayload = {
      id: input.id ?? crypto.randomUUID(),
      title: input.title,
      message: input.message,
      priority: input.priority,
      actionLabel: input.actionLabel,
      actionHandler: input.actionHandler,
      sticky: input.sticky ?? true,
      createdAt: Date.now(),
    };
    this.queue.push(payload);
    this.emitSorted();
  }

  /** Remove one by id */
  dismiss(id: string) {
    this.queue = this.queue.filter((n) => n.id !== id);

    // const queue = this.queue.find((n) => n.id !== id);
    this.closeNotification.push(id);
    this.setCloseNotification();
    // if (queue && queue.id) {
    // }

    this.emitSorted();
  }

  private setCloseNotification() {
    localStorage.setItem(
      NotificationStorageEnum.closeNotification,
      JSON.stringify(this.closeNotification)
    );
  }

  getClosedNotification(): string[] {
    const data = localStorage.getItem(
      NotificationStorageEnum.closeNotification
    );

    return data ? JSON.parse(data) : [];
  }

  /** Clear all */
  clear() {
    this.queue = [];
    this.emitSorted();
  }

  loginClear() {
    this.loginNotificationQueue = [];
    this.loginEmitSorted();
  }
  /** Optionally persist to localStorage */
  persist() {
    localStorage.setItem(
      NotificationStorageEnum.home,
      JSON.stringify(this.queue)
    );
  }

  removeDashboardNotify() {
    localStorage.removeItem(NotificationStorageEnum.home);
  }

  loginPersist() {
    localStorage.setItem(
      NotificationStorageEnum.login,
      JSON.stringify(this.queue)
    );
  }

  /** Load persisted */
  load() {
    try {
      const raw = localStorage.getItem(NotificationStorageEnum.home);
      if (raw) this.queue = JSON.parse(raw);
      this.emitSorted();
    } catch {
      /* ignore */
    }
  }

  /** Sort by priority then recency */
  private emitSorted() {
    const order: NotificationPriority[] = [
      PriorityEnum.Critical,
      PriorityEnum.Warning,
      PriorityEnum.Info,
    ];
    const sorted = [...this.queue].sort((a, b) => {
      const p = order.indexOf(a.priority) - order.indexOf(b.priority);
      if (p !== 0) return p;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    });

    this.notificationsSubject.next(sorted);
  }

  loadLoginNotification() {
    try {
      const raw = localStorage.getItem(NotificationStorageEnum.login);
      if (raw) this.loginNotificationQueue = JSON.parse(raw);
      this.loginEmitSorted();
    } catch {
      /* ignore */
    }
  }

  private loginEmitSorted() {
    const order: NotificationPriority[] = [
      PriorityEnum.Critical,
      PriorityEnum.Warning,
      PriorityEnum.Info,
    ];
    const sorted = [...this.loginNotificationQueue].sort((a, b) => {
      const p = order.indexOf(a.priority) - order.indexOf(b.priority);
      if (p !== 0) return p;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    });
    this.loginNotificationsSubject.next(sorted);
  }
}
